import { cn } from '@/lib/utils'
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react'

interface BoundaryCondition {
  description: string
  value: string | number
  status: 'GOOD' | 'AT_RISK' | 'CRITICAL'
}

interface BoundaryConditionsProps {
  conditions: BoundaryCondition[]
  title?: string
  className?: string
}

const statusConfig = {
  GOOD: {
    icon: CheckCircle,
    bgColor: 'bg-risk-excellent/10',
    textColor: 'text-risk-excellent',
    borderColor: 'border-risk-excellent',
    label: 'GOOD',
  },
  AT_RISK: {
    icon: AlertTriangle,
    bgColor: 'bg-risk-fair/10',
    textColor: 'text-risk-fair',
    borderColor: 'border-risk-fair',
    label: 'AT RISK',
  },
  CRITICAL: {
    icon: XCircle,
    bgColor: 'bg-risk-critical/10',
    textColor: 'text-risk-critical',
    borderColor: 'border-risk-critical',
    label: 'CRITICAL',
  },
}

export function BoundaryConditions({
  conditions,
  title = 'Boundary Conditions',
  className,
}: BoundaryConditionsProps) {
  return (
    <div className={cn('bg-white rounded-lg border p-4', className)}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      <div className="space-y-2">
        {conditions.map((condition, index) => {
          const config = statusConfig[condition.status]
          const Icon = config.icon
          return (
            <div
              key={index}
              className={cn(
                'flex items-center justify-between p-2 rounded border-l-4',
                config.bgColor,
                config.borderColor
              )}
            >
              <div className="flex items-center gap-2 flex-1">
                <Icon className={cn('h-4 w-4 shrink-0', config.textColor)} />
                <span className="text-xs text-gray-700 truncate">{condition.description}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-medium text-gray-900">
                  {typeof condition.value === 'number'
                    ? condition.value.toLocaleString()
                    : condition.value}
                </span>
                <span className={cn('text-[10px] font-semibold', config.textColor)}>
                  {config.label}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
